import { Activity, TrendingDown, TrendingUp } from "lucide-react";

// Static color map — never use template-string Tailwind classes (they get purged in production)
const COLOR_MAP = {
  blue:   { bg: "bg-blue-100",   text: "text-blue-700"   },
  green:  { bg: "bg-green-100",  text: "text-green-700"  },
  red:    { bg: "bg-red-100",    text: "text-red-700"    },
  orange: { bg: "bg-orange-100", text: "text-orange-700" },
  yellow: { bg: "bg-yellow-100", text: "text-yellow-700" },
  purple: { bg: "bg-purple-100", text: "text-purple-700" },
  cyan:   { bg: "bg-cyan-100",   text: "text-cyan-700"   },
  indigo: { bg: "bg-indigo-100", text: "text-indigo-700" },
  slate:  { bg: "bg-slate-100",  text: "text-slate-600"  },
  gray:   { bg: "bg-gray-100",   text: "text-gray-600"   },
};

/**
 * Shared status / metric card.
 *
 * Props:
 *   title    – Card heading
 *   value    – Numeric or string value
 *   unit     – Unit label shown after the value
 *   icon     – Lucide icon component
 *   color    – Key in COLOR_MAP (default "blue")
 *   status   – Optional status label below the value
 *   subtitle – Optional secondary label below the title
 *   trend    – Optional numeric trend percentage (+/-)
 *   decimals – Decimal places for numeric values (default 1)
 *   layout   – "default" (icon badge on right) | "inline" (icon inline with title)
 */
const StatusCard = ({
  title,
  value,
  unit,
  icon: Icon,
  color = "blue",
  status,
  subtitle,
  trend,
  decimals = 1,
  layout = "default",
}) => {
  const colors = COLOR_MAP[color] ?? COLOR_MAP.gray;
  const displayValue =
    typeof value === "number" ? value.toFixed(decimals) : value || "N/A";

  if (layout === "inline") {
    return (
      <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 p-6 hover:-translate-y-0.5 transition-transform duration-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className={`p-1.5 rounded-lg ${colors.bg}`}>
                <Icon className={`w-3.5 h-3.5 ${colors.text}`} />
              </div>
              <p className="text-xs font-bold text-on-surface-variant uppercase tracking-wide">{title}</p>
            </div>
            {subtitle && <p className="text-xs text-outline mb-2">{subtitle}</p>}
            <div className="flex items-baseline gap-1.5 mt-2">
              <p className={`text-2xl font-extrabold font-headline ${colors.text}`}>{displayValue}</p>
              {unit && <span className="text-sm text-on-surface-variant">{unit}</span>}
            </div>
            {trend !== undefined && (
              <div className="flex items-center gap-1 mt-2">
                {trend > 0 ? (
                  <TrendingUp className="w-3.5 h-3.5 text-green-600" />
                ) : trend < 0 ? (
                  <TrendingDown className="w-3.5 h-3.5 text-red-600" />
                ) : null}
                <span className={`text-xs font-semibold ${trend > 0 ? "text-green-600" : trend < 0 ? "text-red-600" : "text-on-surface-variant"}`}>
                  {trend > 0 ? "+" : ""}{trend?.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default layout
  return (
    <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 p-6 hover:-translate-y-0.5 transition-transform duration-200">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest truncate">{title}</p>
          {subtitle && <p className="text-xs text-outline mt-0.5 truncate">{subtitle}</p>}
          <div className="flex items-baseline gap-1.5 mt-2">
            <p className={`text-2xl font-extrabold font-headline ${colors.text}`}>{displayValue}</p>
            {unit && <span className="text-sm text-on-surface-variant">{unit}</span>}
          </div>
          {status && (
            <p className={`text-[10px] font-bold uppercase tracking-wide mt-1 ${colors.text}`}>{status}</p>
          )}
          {trend !== undefined && (
            <div className="flex items-center gap-1 mt-1.5">
              <Activity className={`w-3 h-3 ${trend > 0 ? "text-green-500" : "text-red-500"}`} />
              <span className={`text-xs font-semibold ${trend > 0 ? "text-green-600" : "text-red-600"}`}>
                {trend > 0 ? "+" : ""}{trend.toFixed(1)}%
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full flex-shrink-0 ml-4 ${colors.bg}`}>
          <Icon className={`w-5 h-5 ${colors.text}`} />
        </div>
      </div>
    </div>
  );
};

export default StatusCard;
